import { useState } from 'react';
import { Link } from 'react-router-dom';
import { claimAthleteRecord, createTeamInvite } from '../../api/client';
import { useBusy } from '../../hooks/useBusy';
import { inlineError } from '../../hooks/useApiCall';
import { useToast } from '../../hooks/useToast';
import { useI18n } from '../../i18n';
import { personName } from '../../utils/personName';
import { refusalOf } from '../../lib/httpError';
import Notice from './Notice';

/** #658 — the record that a create found: the same birth date and the same
 *  names, also with another case or a Latin letter for a Lithuanian one. An
 *  archived record is not public: its 409 holds `is_active: false` only. */
export type IdentityMatch =
  | { is_active: true; athlete_record_id: string; first_name: string; last_name: string; team_id: string | null; team_name: string | null }
  | { is_active: false; athlete_record_id: null };

/** The match of a 409 ATHLETE_IDENTITY_EXISTS, or null for any other error. */
export function identityMatchOf(err: unknown): IdentityMatch | null {
  const refusal = refusalOf(err);
  if (refusal?.code !== 'ATHLETE_IDENTITY_EXISTS' || !refusal.params) return null;
  const params: Record<string, unknown> = refusal.params;
  if (params.is_active === false) return { is_active: false, athlete_record_id: null };
  if (!params.athlete_record_id) return null;
  return params as unknown as IdentityMatch;
}

interface Props {
  match: IdentityMatch;
  /** The team of the form. A coach of it may invite the athlete. */
  teamId: string;
  canInvite: boolean;
  onInvited: () => void;
  /** The person registers the own athlete profile: the next step is the
   *  claim, with the birth date that the person typed in the form. */
  forSelf?: { birthDate: string; onClaimed: () => void };
}

/**
 * #658 — the create form found the person. The coach invites the athlete to
 * the team (the athlete, or for a record with no account an admin or a team
 * manager, approves). An archived record needs an admin, who restores it.
 */
export default function ExistingAthleteNotice({ match, teamId, canInvite, onInvited, forSelf }: Props) {
  const { t } = useI18n();
  const x = t.teams.existing;
  const { showSuccess } = useToast();
  const sending = useBusy();
  const [error, setError] = useState('');

  if (!match.is_active) {
    return (
      <Notice tone="warning" data-testid="existing-athlete">{x.archivedFound}</Notice>
    );
  }
  const found = match;
  const name = personName(found.first_name, found.last_name);

  async function claim() {
    if (!forSelf) return;
    await sending.run(async () => {
      setError('');
      try {
        await claimAthleteRecord(found.athlete_record_id, forSelf.birthDate);
        showSuccess(t.teams.selfClaimSubmitted);
        forSelf.onClaimed();
      } catch (err) { setError(inlineError(err)); }
    });
  }

  async function invite() {
    await sending.run(async () => {
      setError('');
      try {
        await createTeamInvite(teamId, found.athlete_record_id);
        showSuccess(t.teams.invite.sent.replace('{name}', name));
        onInvited();
      } catch (err) { setError(inlineError(err)); }
    });
  }

  // The error is a sibling of the warning, not a child. A live region inside a live region
  // can be announced twice, or not at all.
  return (
    <>
      <Notice tone="warning" data-testid="existing-athlete" className="space-y-2">
        <p>
          {x.found}{' '}
          <Link to={`/athletes/${found.athlete_record_id}`} className="font-semibold underline">{name}</Link>
          {' · '}{found.team_name ?? t.teams.invite.noTeam}
        </p>
        {forSelf && (
          <>
            <p>{x.claim}</p>
            <button type="button" onClick={claim} disabled={sending.busy} data-testid="existing-athlete-claim"
              className="btn-primary btn-sm">{t.teams.thisIsMe}</button>
          </>
        )}
        {!forSelf && found.team_id === teamId && <p>{x.sameTeam}</p>}
        {!forSelf && found.team_id !== teamId && canInvite && (
          <button type="button" onClick={invite} disabled={sending.busy} data-testid="existing-athlete-invite"
            className="btn-primary btn-sm">{x.invite}</button>
        )}
      </Notice>
      {error && <Notice tone="error" plain className="mt-2">{error}</Notice>}
    </>
  );
}
